import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth APIs
export const authAPI = {
  studentLogin: (data) => api.post('/auth/student-login', data),
  adminLogin: (data) => api.post('/auth/staff-login', data),
  getMe: () => api.get('/auth/me'),
  registerInit: (data) => api.post('/auth/register/init', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
};

// Payment APIs
export const paymentAPI = {
  initiate: (data) => api.post('/payment/initiate', data),
  verify: (params) => api.get('/payment/verify', { params }),
  getMyPayments: () => api.get('/payment/my-payments'),
};

// Admin/Staff APIs
export const adminAPI = {
  getDashboardStats: () => api.get('/staff/dashboard'),
  
  // Students Management
  getStudents: (params) => api.get('/staff/students', { params }),
  getStudentById: (id) => api.get(`/staff/students/${id}`),
  updateStudent: (id, data) => api.patch(`/staff/students/${id}`, data),
  approveStudent: (id) => api.patch(`/staff/students/${id}/approve`),
  rejectStudent: (id, data) => api.patch(`/staff/students/${id}/reject`, data),
  deleteStudent: (id) => api.delete(`/staff/students/${id}`),
  createStudent: (data) => api.post('/staff/students', data),
  getStudentPayments: (id) => api.get(`/staff/students/${id}/payments`),
  getStudentResults: (id) => api.get(`/staff/students/${id}/results`),
  
  // Profile
  getProfile: () => api.get('/staff/profile'),
  updateProfile: (data) => api.patch('/staff/profile', data),
  changePassword: (data) => api.patch('/staff/profile/password', data),
  uploadStudentPicture: (formData) => api.post('/staff/upload/student-picture', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  
  // Results
  getResults: (params) => api.get('/staff/results', { params }),
  createResult: (data) => api.post('/staff/results', data),
  updateResult: (id, data) => api.patch(`/staff/results/${id}`, data),
  deleteResult: (id) => api.delete('/staff/results/${id}'),
  
  // Attendance
  getAttendance: (params) => api.get('/staff/attendance', { params }),
  markAttendance: (data) => api.post('/staff/attendance', data),
  
  // Subjects
  getSubjects: (params) => api.get('/staff/subjects', { params }),
  createSubject: (data) => api.post('/staff/subjects', data),
  updateSubject: (id, data) => api.patch(`/staff/subjects/${id}`, data),
  deleteSubject: (id) => api.delete(`/staff/subjects/${id}`),
  
  // Notifications / Announcements
  getAnnouncements: () => api.get('/staff/announcements'),
  createAnnouncement: (data) => api.post('/staff/announcements', data),
  deleteAnnouncement: (id) => api.delete(`/staff/announcements/${id}`),
};

// Results APIs
export const resultsAPI = {
  upload: (data) => api.post('/results/upload', data),
  getStudentResults: (studentId, params) => api.get(`/results/${studentId}`, { params }),
  getClassResults: (params) => api.get('/results', { params }),
};

// Attendance APIs
export const attendanceAPI = {
  mark: (data) => api.post('/attendance/mark', data),
  getStudentAttendance: (studentId, params) => api.get(`/attendance/student/${studentId}`, { params }),
  getClassAttendance: (params) => api.get('/attendance', { params }),
};

// Student APIs
export const studentAPI = {
  getProfile: () => api.get('/student/profile'),
  updateProfile: (data) => {
    const isFormData = data instanceof FormData;
    return api.patch('/student/profile', data, {
      headers: isFormData ? {
        'Content-Type': 'multipart/form-data',
      } : {
        'Content-Type': 'application/json',
      },
    });
  },
  getResults: () => api.get('/student/results'),
  getAttendance: () => api.get('/student/attendance'),
};

export default api;

